
import React, { useState, useEffect } from 'react';
import { Flame } from 'lucide-react';

interface FireTextProps {
  text: string;
  className?: string;
}

const FireText: React.FC<FireTextProps> = ({ text, className = "" }) => {
  const [state, setState] = useState<'normal' | 'fire' | 'burning' | 'disappeared' | 'restoring'>('normal');
  
  useEffect(() => {
    // Initial delay before starting the fire effect
    const initialTimeout = setTimeout(() => {
      startFireCycle();
    }, 3000); // 3 seconds initial delay
    
    return () => clearTimeout(initialTimeout);
  }, []);
  
  const startFireCycle = () => {
    // Start the fire
    setState('fire');
    
    // After 1 second, start burning
    setTimeout(() => {
      setState('burning');
      
      // After 2 more seconds, text disappears
      setTimeout(() => {
        setState('disappeared');
        
        // After 1 second of disappearance, start restoring
        setTimeout(() => {
          setState('restoring');
          
          // After restoration is complete, go back to normal
          setTimeout(() => {
            setState('normal');
            
            // Wait a bit and then start the cycle again
            setTimeout(startFireCycle, 4000);
          }, 1000);
        }, 1000);
      }, 2000);
    }, 1000);
  };
  
  // Determine the appropriate className based on the current state
  const getStateClassName = () => {
    switch (state) {
      case 'normal':
        return 'text-cs-dark-grey';
      case 'fire':
        return 'text-cs-dark-grey relative fire-starting';
      case 'burning':
        return 'text-orange-500 burning relative';
      case 'disappeared':
        return 'opacity-0 transition-opacity duration-500';
      case 'restoring':
        return 'text-cs-dark-grey opacity-0 animate-fade-in';
      default:
        return 'text-cs-dark-grey';
    }
  };
  
  return (
    <span className={`relative inline-block ${className} ${getStateClassName()}`}>
      {(state === 'fire' || state === 'burning') && (
        <span className="absolute -top-4 left-1/2 transform -translate-x-1/2 text-orange-500">
          <Flame 
            size={state === 'fire' ? 18 : 24} 
            className={`${state === 'burning' ? 'text-orange-600 animate-pulse' : 'text-orange-500'}`}
          />
        </span>
      )}
      {text}
      {state === 'burning' && (
        <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-orange-300 via-orange-500 to-orange-300"></span>
      )}
    </span>
  );
};

export default FireText;
